"use client"

import * as React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import Image from "next/image"
import Turnstile from "react-turnstile"
import { useTranslations, useLanguage } from "@/components/I18nProvider"
import {
  Button,
  Input,
  Textarea,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Alert,
  AlertDescription,
} from "@sarkiai/ui"

import { toast } from "sonner"
import { Loader, Mail, Phone, User, Send, CheckCircle2 } from "lucide-react"

const contactSchema = z.object({
  fullName: z.string().min(2, "Name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  content: z.string().min(10, "Message must be at least 10 characters long"),
})

type ContactFormValues = z.infer<typeof contactSchema>

const ContactForm: React.FC = () => {
  const t = useTranslations("contact")
  const errorT = useTranslations("error")
  const { locale } = useLanguage()

  const [isPending, setIsPending] = React.useState(false)
  const [isSuccess, setIsSuccess] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      content: "",
    },
  })

  const handleSubmit = async (values: ContactFormValues) => {
    setIsPending(true)
    setError(null)
    setIsSuccess(false)

    try {
      const data = await fetch(
        process.env.NEXT_PUBLIC_API_V1_CONTACT_SUBMIT || '/api/v1/contact',
        {
          method: 'POST',
          body: JSON.stringify(values),
          headers: { 'Content-Type': 'application/json' }
        }
      )

      const result = await data.json()

      if (result?.success) {
        setIsSuccess(true)
        form.reset()
        toast.success(t('success_message'))
      } else {
        setError(result?.reason || errorT('contact_generic_error'))
      }
    } catch (e) {
      console.error(e)
      setError(errorT('contact_generic_error'))
    } finally {
      setIsPending(false)
    }
  }

  return (
    <section id="contact-section" className="py-6 md:py-12">
      <div className="p-6 md:p-10">
        <div className="flex flex-col lg:flex-row items-start gap-12 justify-between">
          <div className="w-full lg:w-3/5">
            <div className="space-y-4 mb-8">
              <h1 className="text-4xl md:text-6xl  tracking-tighter leading-none text-foreground">
                {t('title')}
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {t('subtitle')}
              </p>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-black tracking-widest opacity-60 ml-1">{t('full_name')} *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            className="!bg-background border border-muted-foreground/30 dark:border-white/20 pl-10 h-12 rounded-xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0"
                            placeholder="JOHN DOE"
                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-black tracking-widest opacity-60 ml-1">{t('email')} *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              {...field}
                              type="email"
                              className="!bg-background border border-muted-foreground/30 dark:border-white/20 pl-10 h-12 rounded-xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0"
                              placeholder="john@example.com"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-black tracking-widest opacity-60 ml-1">{t('phone')}</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              {...field}
                              className="!bg-background border border-muted-foreground/30 dark:border-white/20 pl-10 h-12 rounded-xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0"
                              placeholder="(+237) 6xx xxx xxx"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="content"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-black tracking-widest opacity-60 ml-1">{t('content')} *</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          className="!bg-background border border-muted-foreground/30 dark:border-white/20 p-4 min-h-[150px] rounded-2xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0 resize-none"
                          placeholder={t('content_placeholder') || "Votre message..."}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {error && (
                  <Alert variant="destructive" className="bg-destructive/10 border-destructive/20 text-destructive rounded-xl">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {isSuccess && (
                  <Alert className="bg-primary/10 border-primary/20 text-primary rounded-xl">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>{t('success_message')}</AlertDescription>
                  </Alert>
                )}

                {process.env.NODE_ENV === "production" && (
                  <div className="flex justify-center md:justify-start">
                    <Turnstile
                      theme="auto"
                      language={locale}
                      sitekey={process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSITE_SITE_KEY!}
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isPending}
                  className="w-full h-12 bg-black hover:bg-black/80 text-white rounded-xl text-sm font-bold gap-2"
                >
                  {isPending ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      {t('submitting') || "Envoi..."}
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5" />
                      {t('submit')}
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </div>

          <div className="hidden lg:block w-full lg:w-2/5">
            <div className="relative aspect-[2/3] rounded-4xl overflow-hidden shadow-2xl group transition-transform duration-700 hover:scale-105">
              <Image
                alt="Contact"
                fill
                src="/assets/images/hero-corporate/ceo-djotta-portrait-1.webp"
                className="object-cover"
              />
              <div className="absolute inset-0 bg-black/10 hover:bg-transparent transition-colors duration-700" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ContactForm
